/*
  Shows "does this phone support eSIM?" where the site talks about compatibility.
  The check comes from the app (window.PhonelynePhoneCheck, loaded from
  [App URL]/phone-check.js). Places on the page opt in with:
    [data-phone-check-button]   a "Check compatibility" link that becomes the result
    [data-phone-check-verdict]  a note (plan page, basket)
    [data-phone-check-card]     the "Detected on this device" card (devices page)
  plus any link to the devices page inside help answers (data-phone-check-help).
  Each carries data-surface, which is also what gets counted (once per visit).
  Advice only: nothing here ever blocks adding to the basket or checking out.
*/
(function () {
  var DEVICES_URL = '/pages/compatible-devices';

  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function phoneName(check) {
    return check.device === 'This iPhone' ? 'iPhone' : check.device;
  }

  /** The words for a result. `kind`: ok | warn | neutral. */
  function words(check) {
    if (check.kind === 'supported') {
      return {
        kind: 'ok',
        short: 'Your ' + phoneName(check) + ' supports eSIM',
        title: 'Your ' + phoneName(check) + ' supports eSIM',
        text: check.detail ? check.detail + '.' : ''
      };
    }
    if (check.kind === 'not_supported' && check.likely) {
      var brand = check.device.replace(/ phone$/, '');
      return {
        kind: 'warn',
        short: 'This phone may not support eSIM',
        title: 'This phone probably doesn\u2019t support eSIM',
        text: 'Most ' + brand + ' phones only take a physical SIM. Dial *#06# to be sure: if an EID number appears, it does. Buying for another phone? That\u2019s fine.'
      };
    }
    if (check.kind === 'not_supported') {
      return {
        kind: 'warn',
        short: 'This phone doesn\u2019t support eSIM',
        title: 'This ' + check.device + ' doesn\u2019t support eSIM',
        text: 'Buying for a different phone? That\u2019s fine: we\u2019ll email you a QR code to scan on it.'
      };
    }
    return null;
  }

  function note(kind, title, text, link) {
    var icon = kind === 'ok' ? '\u2713' : kind === 'warn' ? '!' : 'i';
    return (
      '<span class="pc-note__icon" aria-hidden="true"><i>' + icon + '</i></span>' +
      '<span class="pc-note__body"><strong>' + esc(title) + '</strong>' +
      (text ? '<span>' + esc(text) + '</span>' : '') +
      (link ? '<a href="' + DEVICES_URL + '">' + esc(link) + '</a>' : '') +
      '</span>'
    );
  }

  function showNote(el, kind, title, text, link) {
    el.className = 'pc-note pc-note--' + kind;
    el.innerHTML = note(kind, title, text, link);
    el.hidden = false;
    el.setAttribute('role', 'status');
  }

  // A "Check compatibility" link. Turns into the result on a phone we recognise;
  // on the plan page an unsupported phone is flagged too. Otherwise left as it is.
  function renderButton(el, check) {
    var w = words(check);
    if (!w) return;
    if (w.kind === 'warn' && el.getAttribute('data-surface') !== 'plan') return;
    el.classList.add('pc-pill', 'pc-pill--' + w.kind);
    el.setAttribute('aria-label', w.short + '. See compatible devices');
    el.innerHTML = '<span aria-hidden="true">' + (w.kind === 'ok' ? '\u2713' : '!') + '</span> ' + esc(w.short);
  }

  function renderVerdict(el, check) {
    var surface = el.getAttribute('data-surface');
    var w = words(check);
    if (w) return showNote(el, w.kind, w.title, w.text);
    if (surface !== 'cart') return; // plan page: the header link still offers the check
    if (check.kind === 'unknown' && check.platform === 'computer') {
      showNote(el, 'neutral', 'You\u2019ll install it on your phone', 'We email you a QR code to scan on the phone you travel with.', 'Check that phone supports eSIM');
    } else {
      showNote(el, 'neutral', 'Check your phone supports eSIM before you pay', '', 'Check compatibility');
    }
  }

  function renderCard(el, check) {
    var w = words(check);
    var html;
    if (w) {
      html = note(w.kind, w.title, w.text);
      el.className = 'pc-card pc-note pc-note--' + w.kind;
    } else {
      var computer = check.kind === 'unknown' && check.platform === 'computer';
      var iphone = check.kind === 'unknown' && check.platform === 'iphone';
      var title = computer ? 'Which phone will you travel with?' : 'We couldn\u2019t tell from this browser';
      var text = computer
        ? 'You\u2019re on a computer, so find the phone you\u2019ll travel with in the lists below.'
        : iphone
          ? 'iPhone XR, XS and newer support eSIM. Find your model in Settings, then General, then About, and check it below.'
          : 'Find your phone in the lists below. Or dial *#06#: if an EID number appears, it supports eSIM.';
      html = note('neutral', title, text);
      el.className = 'pc-card pc-note pc-note--neutral';
    }
    el.innerHTML = '<span class="pc-card__eyebrow">Detected on this device</span><div class="pc-card__row">' + html + '</div>';
    el.hidden = false;
  }

  // Help answers that link to the devices page get one personal line underneath.
  function markHelpLinks() {
    var links = document.querySelectorAll('main a[href*="' + DEVICES_URL + '"]:not(.btn):not([data-phone-check-button])');
    Array.prototype.forEach.call(links, function (a) {
      var block = a.closest('p, li, .rte, details, .help-article__body') || a.parentElement;
      if (!block || block.closest('[data-phone-check-card], .compat, footer, header')) return;
      if (block.nextElementSibling && block.nextElementSibling.hasAttribute('data-phone-check-help')) return;
      var slot = document.createElement('div');
      slot.setAttribute('data-phone-check-help', '');
      slot.setAttribute('data-surface', 'help');
      slot.hidden = true;
      block.insertAdjacentElement('afterend', slot);
    });
  }

  // Counted against the most specific place on the page (a plan page also has call-outs).
  var PRIORITY = ['plan', 'cart', 'devices', 'destinations', 'help', 'cta'];
  function surfaceOf(places) {
    var found = Array.prototype.map.call(places, function (el) { return el.getAttribute('data-surface'); });
    for (var i = 0; i < PRIORITY.length; i++) if (found.indexOf(PRIORITY[i]) !== -1) return PRIORITY[i];
    return 'help';
  }

  function run() {
    var api = window.PhonelynePhoneCheck;
    if (!api) return;
    markHelpLinks();
    var places = document.querySelectorAll('[data-phone-check-button], [data-phone-check-verdict], [data-phone-check-card], [data-phone-check-help]');
    if (!places.length) return;
    api.detect().then(function (check) {
      Array.prototype.forEach.call(places, function (el) {
        if (el.hasAttribute('data-phone-check-button')) renderButton(el, check);
        else if (el.hasAttribute('data-phone-check-card')) renderCard(el, check);
        else if (el.hasAttribute('data-phone-check-help')) {
          var w = words(check);
          if (w) showNote(el, w.kind, w.title, w.text);
        } else renderVerdict(el, check);
      });
      api.report(check, surfaceOf(places));
    });
  }

  // Either script can load first.
  if (window.PhonelynePhoneCheck) run();
  else document.addEventListener('phonelyne:phone-check-ready', run, { once: true });
})();
